// Service taxonomy, read live from the database (Run A, 2026-08-18).
//
// This replaces the retired frontend constant PROJECT_WORK_SERVICE_TYPES. The
// vocabulary now has exactly one home — service_divisions (axis='work') and
// service_catalog — and project_services.service_code carries a VALIDATED FK to
// it, so anything this picker can produce is something the database will accept
// and nothing else can be written.
//
// Run B Phase 2 (2026-08-18): EXTENDED, not replaced, to also carry the
// non-project vocabulary the AM entry screen needs — the five special divisions
// and their reasons. Those AM-facing labels were renamed on 2026-08-18 and live
// in service_divisions.display_name; they are read here and NEVER written into
// this file. Hardcoding them in the frontend is precisely how the old constant
// drifted from 56 variants to 77 with a dropdown already live.
//
// The division -> event_class mapping is not restated here either: it is one
// CASE inside the database (event_class_for_division) and reaches the app
// through v_event_classes.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';

export const OTHER_SERVICE_CODE = 'OTHER';

/**
 * The division holding standing, recurring work — what a building buys every
 * month, as opposed to a job done once.
 *
 * ⛔ It sits on its OWN axis, not on `work`. The division picker above fetches
 * `axis='work'`, so a recurring division on that axis would have appeared on the
 * one-off project screen and offered day porter on a strip-and-wax. Ben,
 * 2026-09-16. Keep the axes apart and there is nothing to remember.
 */
export const RECURRING_DIVISION_ID = 'RECURRING';

/** projects.event_class for ordinary billable service work. */
export const PROJECT_EVENT_CLASS = 'Project';

/**
 * projects.event_class for the division that corrects someone's arithmetic.
 *
 * Run C Phase 1. This is a STRUCTURAL value, not a label: it is one of the seven
 * strings enumerated by the `projects_event_class_check` CHECK constraint, in the
 * same class as PROJECT_EVENT_CLASS above. The AM-facing WORDS for this division
 * are read from v_event_classes.display_name like every other division's, and are
 * never written in the frontend.
 *
 * It is named here because this is the one non-project division that can still
 * bill the client — two of its six reasons do — and the entry screen has to say
 * so in the same breath as "most of these are not billed". Deriving that sentence
 * from the database rather than typing the division's name keeps the rename-proof
 * property the rest of this file has.
 */
export const CORRECTION_EVENT_CLASS = 'Correction';

export interface ServiceDivision {
  division_id: string;
  display_name: string;
  axis: string;
  sort_order: number;
}

export interface ServiceCatalogEntry {
  service_code: string;
  division_id: string | null;
  display_name: string;
  default_unit: string;
  requires_note: boolean;
  sort_order: number;
}

/** A non-project reason to record money: the five special divisions. */
export interface EventClassOption {
  division_id: string;
  display_name: string;
  description: string | null;
  event_class: string;
  sort_order: number;
}

export interface SpecialReason {
  reason_code: string;
  division_id: string;
  display_name: string;
  sort_order: number;
}

export interface ServiceCatalogResult {
  divisions: ServiceDivision[];
  services: ServiceCatalogEntry[];
  /** The OTHER escape-hatch entry, if it is active. */
  other: ServiceCatalogEntry | null;
  /**
   * The standing-work codes, for the building's own service lines.
   *
   * ⛔ Narrowed from the set already fetched, never by filtering the fetch —
   * filtering the fetch is what blanked 81 saved project lines (TCSD-0171).
   */
  recurringServices: ServiceCatalogEntry[];
  /** The five non-project classes, in the order the database sorts them. */
  eventClasses: EventClassOption[];
  reasons: SpecialReason[];
  reasonsForDivision: (divisionId: string | null) => SpecialReason[];
  servicesForDivision: (divisionId: string | null) => ServiceCatalogEntry[];
  labelFor: (serviceCode: string | null | undefined) => string | null;
  divisionForService: (serviceCode: string | null | undefined) => string | null;
  isLoading: boolean;
  error: string | null;
}

export function useServiceCatalog(): ServiceCatalogResult {
  const [divisions, setDivisions] = useState<ServiceDivision[]>([]);
  const [services, setServices] = useState<ServiceCatalogEntry[]>([]);
  const [eventClasses, setEventClasses] = useState<EventClassOption[]>([]);
  const [reasons, setReasons] = useState<SpecialReason[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [divRes, svcRes, ecRes, reasonRes] = await Promise.all([
          supabase
            .from('service_divisions')
            .select('division_id, display_name, axis, sort_order')
            .eq('axis', 'work')
            .eq('is_active', true)
            .order('sort_order'),
          supabase
            .from('service_catalog')
            .select('service_code, division_id, display_name, default_unit, requires_note, sort_order')
            .eq('is_active', true)
            .order('sort_order'),
          supabase
            .from('v_event_classes')
            .select('division_id, display_name, description, event_class, sort_order')
            .order('sort_order'),
          supabase
            .from('special_reasons')
            .select('reason_code, division_id, display_name, sort_order')
            .eq('is_active', true)
            .order('sort_order'),
        ]);
        if (cancelled) return;
        if (divRes.error) throw divRes.error;
        if (svcRes.error) throw svcRes.error;
        if (ecRes.error) throw ecRes.error;
        if (reasonRes.error) throw reasonRes.error;
        setDivisions((divRes.data ?? []) as ServiceDivision[]);
        setServices((svcRes.data ?? []) as ServiceCatalogEntry[]);
        setEventClasses((ecRes.data ?? []) as unknown as EventClassOption[]);
        setReasons((reasonRes.data ?? []) as SpecialReason[]);
      } catch (err) {
        if (cancelled) return;
        setError(errorMessage(err, 'Failed to load the service catalog.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const reasonsForDivision = useCallback(
    (divisionId: string | null) =>
      divisionId ? reasons.filter((r) => r.division_id === divisionId) : [],
    [reasons],
  );

  return useMemo(() => {
    const byCode = new Map(services.map((s) => [s.service_code, s]));
    return {
      divisions,
      services,
      other: byCode.get(OTHER_SERVICE_CODE) ?? null,
      recurringServices: services.filter((s) => s.division_id === RECURRING_DIVISION_ID),
      eventClasses,
      reasons,
      reasonsForDivision,
      servicesForDivision: (divisionId: string | null) =>
        divisionId ? services.filter((s) => s.division_id === divisionId) : [],
      labelFor: (serviceCode: string | null | undefined) =>
        serviceCode ? (byCode.get(serviceCode)?.display_name ?? serviceCode) : null,
      divisionForService: (serviceCode: string | null | undefined) =>
        serviceCode ? (byCode.get(serviceCode)?.division_id ?? null) : null,
      isLoading,
      error,
    };
  }, [divisions, services, eventClasses, reasons, reasonsForDivision, isLoading, error]);
}
